import {
  ETIQUETA_ESTADO_BB,
  ETIQUETA_ESTADO_BB_EN,
  ETIQUETA_MOVIMIENTO_MINEROS,
  ETIQUETA_MOVIMIENTO_MINEROS_EN,
  ETIQUETA_ETAPA_MINEROS,
  ETIQUETA_ETAPA_MINEROS_EN,
  type BigBagResumen,
} from '@cnc/contracts';
import {useState, type ReactNode} from 'react';
import {Link, useLocation, useParams, useSearchParams} from 'react-router';
import {useBigBag} from '../api/inventario';
import {useDetalleDeMineros} from '../api/mineros';
import {AYUDA_BIG_BAG as A, AYUDA_FICHA_MINEROS} from '../componentes/ayuda-inventario';
import {AYUDA_MINEROS as AM} from '../componentes/ayuda-mineros';
import {COLUMNAS_DE_INVENTARIO} from '../componentes/columnas-inventario';
import {EdicionDeBigBag} from '../componentes/edicion-big-bag';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {usePermisos} from '../componentes/sesion-contexto';
import {Estado} from '../componentes/tabla-inventario';
import {claveDeTexto, et, tr, trx, type Texto} from '../i18n/idioma';
import {fecha, mes, numero, textoOGuion} from '../utilidades/formato';
import {direccionDeVuelta} from '../utilidades/vuelta';

// Un dato de una ficha: nombre, valor y su nota (i).
type Dato = [string, ReactNode, (Texto | string)?];
const AF = AYUDA_FICHA_MINEROS;

export function PaginaBigBag() {
  const {bbNo = ''} = useParams();
  const consulta = useBigBag(bbNo);
  const [parametros] = useSearchParams();
  // Desde Calidad de datos ("✎ Corregir"): abre ya en edicion y vuelve a la revision.
  const volver = direccionDeVuelta(parametros);
  // Desde la tabla del inventario: volver con los mismos filtros (recorrido 7/10/2026).
  const {state} = useLocation();
  const inventarioConFiltros =
    typeof (state as {volver?: unknown} | null)?.volver === 'string'
      ? (state as {volver: string}).volver
      : '/inventario';

  return (
    <section className="max-w-5xl">
      <Link to={volver ?? inventarioConFiltros} className="text-sm text-verde hover:underline">
        {volver
          ? tr('← Volver a la revisión de calidad', '← Back to the quality check')
          : tr('← Volver al inventario', '← Back to inventory')}
      </Link>
      {consulta.isPending && <p className="mt-4 text-apagado">{tr('Consultando…', 'Loading…')}</p>}
      {consulta.isError && (
        <div className="mt-4 rounded-lg border border-alerta/30 bg-red-50 p-4 text-sm">
          <div className="font-medium text-alerta">
            {tr('No se pudo consultar el Big Bag', 'Could not load the Big Bag')}
          </div>
          <div className="mt-1">{consulta.error.message}</div>
        </div>
      )}
      {consulta.data && <Detalle bolsa={consulta.data} editarAlAbrir={parametros.get('editar') === '1'} />}
    </section>
  );
}

function Detalle({bolsa, editarAlAbrir}: {bolsa: BigBagResumen; editarAlAbrir: boolean}) {
  const permisos = usePermisos();
  const puedeEditarla = bolsa.estado === 'exported' ? permisos.administrar : permisos.editar;
  const [editando, setEditando] = useState(editarAlAbrir && puedeEditarla);
  // Enlaces cruzados (recorrido 7/10/2026): a la exportacion, a su logistica por
  // contenedor y a todos los Big Bags de la recoleccion.
  const enlace = (texto: string | null, a: string) =>
    texto ? (
      <Link to={a} className="text-verde hover:underline">
        {texto}
      </Link>
    ) : (
      '—'
    );
  const deExportacion = bolsa.exportacion ? `/seguimiento/${encodeURIComponent(bolsa.exportacion)}` : '';
  const grupos: Array<{titulo: string; datos: Dato[]}> = [
    {
      titulo: tr('Identificación', 'Identification'),
      datos: [
        [tr('Exportación', 'Export'), enlace(bolsa.exportacion, deExportacion), A.exportacion],
        [
          tr('Recolección', 'Pickup'),
          bolsa.envioDirecto
            ? tr('Envío directo (sin bodega)', 'Direct shipment (no warehouse)')
            : enlace(
                bolsa.recoleccion,
                `/inventario?estado=todos&recoleccion=${encodeURIComponent(bolsa.recoleccion ?? '')}`,
              ),
          A.recoleccion,
        ],
        [
          tr('Contenedor', 'Container'),
          bolsa.exportacion ? enlace(bolsa.contenedor, `${deExportacion}#logistica`) : textoOGuion(bolsa.contenedor),
          A.contenedor,
        ],
        [tr('Sello', 'Seal'), textoOGuion(bolsa.sello), A.sello],
        [tr('Tipo de material', 'Material type'), textoOGuion(bolsa.tipoMaterial), A.tipoMaterial],
        [tr('BB # anterior', 'Previous BB #'), textoOGuion(bolsa.numeroAnterior), A.numeroAnterior],
        [tr('Sello anterior', 'Previous seal'), textoOGuion(bolsa.selloAnterior), A.selloAnterior],
      ],
    },
    {
      titulo: tr('Pesos y tenores', 'Weights and grades'),
      datos: [
        [tr('Toneladas húmedas', 'Wet tons'), numero(bolsa.tonHumedas, 3), A.tonHumedas],
        [tr('Toneladas secas', 'Dry tons'), numero(bolsa.tonSecas, 3), A.tonSecas],
        ['Au (g/t)', numero(bolsa.auGt, 2), A.auGt],
        ['Au (g)', numero(bolsa.auG, 2), A.auG],
        ['Au (oz)', numero(bolsa.auOz, 3), A.auOz],
        ['Ag (g/t)', numero(bolsa.agGt, 2), A.agGt],
        ['Ag (g)', numero(bolsa.agG, 2), A.agG],
        ['Ag (oz)', numero(bolsa.agOz, 3), A.agOz],
      ],
    },
    {
      titulo: tr('Origen', 'Origin'),
      datos: [
        [tr('Mina', 'Mine'), textoOGuion(bolsa.mina), A.mina],
        [tr('Proveedor', 'Supplier'), textoOGuion(bolsa.proveedor), A.proveedor],
        [tr('Exportador', 'Exporter'), textoOGuion(bolsa.exportador), A.exportador],
        [tr('Planta', 'Plant'), textoOGuion(bolsa.planta), A.planta],
        [tr('Municipio', 'Municipality'), textoOGuion(bolsa.municipio), A.municipio],
        [tr('Bodega', 'Warehouse'), textoOGuion(bolsa.ubicacion), A.ubicacion],
      ],
    },
    {
      titulo: tr('Fechas', 'Dates'),
      datos: [
        [tr('Fecha de empaque', 'Packing date'), fecha(bolsa.fechaEmpaque), A.fechaEmpaque],
        [tr('Mes de molienda', 'Grinding month'), mes(bolsa.mesMolienda), A.mesMolienda],
        // Oficial = MES MOLIENDA del Consolidated (decision 0025); el real queda como referencia (decision 0040).
        [tr('Mes molienda real', 'Real grinding month'), mes(bolsa.mesMoliendaReal), A.mesMoliendaReal],
        [tr('Entrada a bodega', 'Warehouse entry'), fecha(bolsa.entradaBodega), A.entradaBodega],
        [tr('Día de llenado', 'Filling date'), fecha(bolsa.fechaLlenado), A.fechaLlenado],
      ],
    },
  ];
  const delArchivo = COLUMNAS_DE_INVENTARIO.filter(c => claveDeTexto(c.grupo) === 'Columnas del archivo')
    .map(c => [trx(c.titulo), c.valor(bolsa), c.ayuda] as Dato)
    .filter(([, valor]) => valor !== '—');

  // Los exportados ya salieron en documentos de aduana: solo un administrador los edita.
  const puedeEditar = bolsa.estado === 'exported' ? permisos.administrar : permisos.editar;

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold text-verde-oscuro">
          Big Bag {bolsa.bbNo}
          <NotaDeAyuda titulo="BB #" texto={A.bbNo} />
        </h1>
        <span>
          <Estado estado={bolsa.estado} texto={et(ETIQUETA_ESTADO_BB, ETIQUETA_ESTADO_BB_EN)[bolsa.estado]} />
          <NotaDeAyuda titulo={tr('Estado', 'Status')} texto={A.estado} />
        </span>
        {puedeEditar && !editando && (
          <button
            type="button"
            onClick={() => setEditando(true)}
            className="ml-auto rounded-md border border-verde bg-white px-3 py-1.5 text-sm text-verde-oscuro hover:bg-verde-claro"
          >
            {tr('✎ Editar', '✎ Edit')}
          </button>
        )}
      </div>
      {editando && <EdicionDeBigBag bolsa={bolsa} onCerrar={() => setEditando(false)} />}
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {grupos.map(grupo => (
          <Tarjeta key={grupo.titulo} titulo={grupo.titulo} datos={grupo.datos} />
        ))}
        {delArchivo.length > 0 && (
          <div className="md:col-span-2">
            <Tarjeta
              titulo={tr('Columnas del archivo consolidado', 'Consolidated file columns')}
              datos={delArchivo}
              columnas={2}
            />
          </div>
        )}
      </div>
      {bolsa.linea === 'mineros' && <DetalleDeMineros bbNo={bolsa.bbNo} />}
    </div>
  );
}

// Big Bag de Mineros (decision 0022): movimientos en la bodega, lotes y tulas.
function DetalleDeMineros({bbNo}: {bbNo: string}) {
  const consulta = useDetalleDeMineros(bbNo);
  const d = consulta.data;
  if (consulta.isError) return <p className="mt-4 text-sm text-alerta">{consulta.error.message}</p>;
  if (!d) return null;
  const x = d.bolsa;
  const movimiento = et(ETIQUETA_MOVIMIENTO_MINEROS, ETIQUETA_MOVIMIENTO_MINEROS_EN);
  const etapa = et(ETIQUETA_ETAPA_MINEROS, ETIQUETA_ETAPA_MINEROS_EN);
  const kg = (v: number | null) => (v === null ? '—' : `${numero(v, 1)} kg`);
  return (
    <div className="mt-4 space-y-4" data-prueba="detalle-mineros">
      {x.novedades.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm">
          <h2 className="font-medium">
            {tr('Novedades para revisar', 'Issues to review')}
            <NotaDeAyuda texto={AM.novedades!} />
          </h2>
          <ul className="mt-1 list-disc pl-5">
            {x.novedades.map(n => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <Tarjeta
          titulo={tr('Mineros: planta y bodega', 'Mineros: plant and warehouse')}
          datos={[
            [
              tr('Etapa hoy', 'Stage today'),
              x.etapa ? etapa[x.etapa] + (x.asignada ? '' : tr(' (disponible)', ' (available)')) : '—',
              AF.etapa,
            ],
            [tr('Lotes con datos', 'Lots with data'), `${x.lotesConDatos} / ${x.lotes}`, AM.lotes],
            [tr('Envío de planta', 'Plant shipment'), textoOGuion(x.envio), AM.envio],
            [tr('Salida de El Bagre', 'Left El Bagre'), fecha(x.salidaPlanta), AM.salidaPlanta],
            [tr('Movimiento', 'Movement'), x.movimiento ? movimiento[x.movimiento] : '—', AM.movimiento],
            [tr('Ingreso a Cartagena', 'Arrived in Cartagena'), fecha(x.ingresoCartagena), AM.ingresoCartagena],
            [
              tr('Tulas recibidas', 'Sacks received'),
              x.tulasEsperadas ? `${x.tulasRecibidas} / ${x.tulasEsperadas}` : '—',
              AM.tulas,
            ],
            [tr('Salida de bodega (llenado)', 'Left warehouse (filling)'), fecha(x.salidaBodega), AM.salidaBodega],
            [tr('Contenedor', 'Container'), textoOGuion(x.contenedor), AM.contenedor],
            ['ETD', fecha(x.etd), AM.etd],
          ]}
        />
        <Tarjeta
          titulo={tr('Bodega: pesos y precintos', 'Warehouse: weights and seals')}
          datos={[
            [tr('Peso al cargue', 'Weight at loading'), kg(d.bodega.pesoCargueKg), AF.pesoCargue],
            [tr('Precinto al cargue', 'Seal at loading'), textoOGuion(d.bodega.precintoCargue), AF.precintoCargue],
            [tr('Peso al ingreso', 'Weight on arrival'), kg(d.bodega.pesoIngresoKg), AF.pesoIngreso],
            [tr('Precinto al ingreso', 'Seal on arrival'), textoOGuion(d.bodega.precintoIngreso), AF.precintoIngreso],
            [tr('Peso al llenado', 'Weight at filling'), kg(d.bodega.pesoLlenadoKg), AF.pesoLlenado],
            [tr('Precinto al llenado', 'Seal at filling'), textoOGuion(d.bodega.precintoLlenado), AF.precintoLlenado],
          ]}
        />
      </div>
      {d.recoleccion && (
        <div className="grid gap-4 md:grid-cols-2" data-prueba="recoleccion-tm">
          <Tarjeta
            titulo={tr(
              `Recolección ${d.recoleccion.tm} (declaración de valor)`,
              `Pickup ${d.recoleccion.tm} (value declaration)`,
            )}
            datos={[
              [tr('Fecha', 'Date'), fecha(d.recoleccion.fecha), AM.tmFecha],
              [tr('Origen', 'Origin'), textoOGuion(d.recoleccion.origen), AF.tmOrigen],
              [tr('Punto de entrega', 'Delivery point'), textoOGuion(d.recoleccion.puntoDeEntrega), AF.tmPunto],
              [tr('Big Bags en la TM', 'Big Bags in the TM'), numero(d.recoleccion.bigBags), AF.tmBigBags],
              [tr('Peso húmedo total', 'Total wet weight'), kg(d.recoleccion.humedoKgTotal), AF.tmHumedo],
              [
                tr('Valor declarado total', 'Total declared value'),
                d.recoleccion.valorUsdTotal === null ? '—' : `USD ${numero(d.recoleccion.valorUsdTotal, 2)}`,
                AF.tmValor,
              ],
              ...Object.entries(d.recoleccion.sellosDelCamion).map(
                ([tipo, valor]) => [tr(`Sello del camión: ${tipo}`, `Truck seal: ${tipo}`), valor, AF.tmSellos] as Dato,
              ),
            ]}
          />
          <Tarjeta
            titulo={tr('Lo declarado para este Big Bag', 'Declared for this Big Bag')}
            datos={[
              [tr('Sello', 'Seal'), textoOGuion(d.recoleccion.sello), AF.declSello],
              [tr('Peso húmedo', 'Wet weight'), kg(d.recoleccion.humedoKg), AF.declHumedo],
              [tr('Peso seco', 'Dry weight'), kg(d.recoleccion.secoKg), AF.declSeco],
              ['Au (g/t)', d.recoleccion.auGt === null ? '—' : numero(d.recoleccion.auGt, 2), AF.declAuGt],
              ['Au (oz)', d.recoleccion.auOz === null ? '—' : numero(d.recoleccion.auOz, 2), AF.declAuOz],
              [
                tr('Valor declarado', 'Declared value'),
                d.recoleccion.valorUsd === null ? '—' : `USD ${numero(d.recoleccion.valorUsd, 2)}`,
                AF.declValor,
              ],
            ]}
          />
        </div>
      )}
      <div className="overflow-x-auto rounded-lg border border-linea bg-white">
        <h2 className="px-4 pt-3 font-medium">{tr('Lotes', 'Lots')}</h2>
        <table className="mt-2 w-full text-sm" data-prueba="lotes-mineros">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-apagado">
              <th className="px-3 py-2 font-semibold">
                {tr('Lote', 'Lot')}
                <NotaDeAyuda texto={AF.lote} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Envío', 'Shipment')}
                <NotaDeAyuda texto={AF.loteEnvio} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Fecha de envío', 'Shipping date')}
                <NotaDeAyuda texto={AF.loteFecha} />
              </th>
              <th className="px-3 py-2 text-right font-semibold">
                {tr('Húmedo (kg)', 'Wet (kg)')}
                <NotaDeAyuda texto={AF.loteHumedo} />
              </th>
              <th className="px-3 py-2 text-right font-semibold">
                {tr('Seco (kg)', 'Dry (kg)')}
                <NotaDeAyuda texto={AF.loteSeco} />
              </th>
              <th className="px-3 py-2 text-right font-semibold">
                Au (g/t)
                <NotaDeAyuda texto={AF.loteAuGt} />
              </th>
              <th className="px-3 py-2 text-right font-semibold">
                Au (g)
                <NotaDeAyuda texto={AF.loteAuG} />
              </th>
              <th className="px-3 py-2 text-right font-semibold">
                Ag (g)
                <NotaDeAyuda texto={AF.loteAgG} />
              </th>
            </tr>
          </thead>
          <tbody>
            {d.lotes.map(l => (
              <tr key={l.codigo} className="border-t border-linea/70">
                <td className="px-3 py-1.5 font-medium">
                  {l.codigo}
                  {!l.conDatos && <span className="ml-1 text-xs text-alerta">{tr('sin datos', 'no data')}</span>}
                </td>
                <td className="px-3 py-1.5">{textoOGuion(l.envio)}</td>
                <td className="px-3 py-1.5 tabular-nums">{fecha(l.fechaEnvio)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{numero(l.humedoKg, 2)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{numero(l.secoKg, 2)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{numero(l.auGt, 2)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{numero(l.auG, 2)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{numero(l.agG, 2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {d.tulas.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-linea bg-white">
          <h2 className="px-4 pt-3 font-medium">{tr(`Tulas (${d.tulas.length})`, `Sacks (${d.tulas.length})`)}</h2>
          <table className="mt-2 w-full text-sm" data-prueba="tulas-mineros">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-apagado">
                <th className="px-3 py-2 font-semibold">
                  {tr('Tula', 'Sack')}
                  <NotaDeAyuda texto={AF.tula} />
                </th>
                <th className="px-3 py-2 font-semibold">
                  {tr('Lote', 'Lot')}
                  <NotaDeAyuda texto={AF.lote} />
                </th>
                <th className="px-3 py-2 font-semibold">
                  {tr('Sellos de planta', 'Plant seals')}
                  <NotaDeAyuda texto={AF.tulaSellos} />
                </th>
                <th className="px-3 py-2 font-semibold">
                  {tr('Ingreso', 'Arrival')}
                  <NotaDeAyuda texto={AF.tulaIngreso} />
                </th>
                <th className="px-3 py-2 font-semibold">
                  {tr('Precinto al ingreso', 'Seal on arrival')}
                  <NotaDeAyuda texto={AF.tulaPrecinto} />
                </th>
                <th className="px-3 py-2 font-semibold">
                  {tr('Salida de bodega', 'Left warehouse')}
                  <NotaDeAyuda texto={AF.tulaSalida} />
                </th>
                <th className="px-3 py-2 font-semibold">
                  {tr('Contenedor', 'Container')}
                  <NotaDeAyuda texto={AF.tulaContenedor} />
                </th>
                <th className="px-3 py-2 font-semibold">
                  {tr('Nota', 'Note')}
                  <NotaDeAyuda texto={AF.tulaNota} />
                </th>
              </tr>
            </thead>
            <tbody>
              {d.tulas.map(t => (
                <tr
                  key={t.codigo}
                  className={`border-t border-linea/70 ${t.cruce === 'sin_registro' ? 'bg-amber-50' : t.cruce === 'sello_distinto' || t.cruce === 'por_sello' ? 'bg-red-50' : ''}`}
                >
                  <td className="px-3 py-1.5 font-medium">{t.codigo}</td>
                  <td className="px-3 py-1.5">{textoOGuion(t.lote)}</td>
                  <td className="px-3 py-1.5">{[t.selloInterno, t.selloExterno].filter(Boolean).join(' / ') || '—'}</td>
                  <td className="px-3 py-1.5 tabular-nums">{fecha(t.ingreso)}</td>
                  <td className="px-3 py-1.5">{textoOGuion(t.precintoIngreso)}</td>
                  <td className="px-3 py-1.5 tabular-nums">{fecha(t.salidaBodega)}</td>
                  <td className="px-3 py-1.5">{textoOGuion(t.contenedor)}</td>
                  <td className="px-3 py-1.5 text-xs">{textoOGuion(t.nota)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Tarjeta({titulo, datos, columnas = 1}: {titulo: string; datos: Dato[]; columnas?: 1 | 2}) {
  return (
    <div className="rounded-lg border border-linea bg-white p-5">
      <h2 className="font-medium">{titulo}</h2>
      <dl className={`mt-3 gap-x-8 gap-y-2 ${columnas === 2 ? 'grid sm:grid-cols-2' : 'space-y-2'}`}>
        {datos.map(([nombre, valor, ayuda]) => (
          <div key={nombre} className="flex justify-between gap-4 text-sm">
            <dt className="text-apagado">
              {nombre}
              {ayuda && <NotaDeAyuda titulo={nombre} texto={ayuda} />}
            </dt>
            <dd className="text-right tabular-nums">{valor}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
